import React from "react";
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  spring,
  interpolate,
  Sequence,
  Audio,
  Img,
  staticFile,
  random,
} from "remotion";
import {SPRINGS, FONT_SCALE, SAFE_ZONES} from "../presets/editing-styles";
import {CLAMP, FONT_STACK, IDV} from "../presets/idv";
import {Noise} from "../components/dimus/Noise";
import {Vignette} from "../components/dimus/Vignette";
import {FilmGrain} from "../components/dimus/FilmGrain";
import {GradientText} from "../components/dimus/GradientText";
import {GlowText} from "../components/dimus/GlowText";
import {SceneTransition} from "../components/dimus/SceneTransition";
import {AccentLine} from "../components/dimus/AccentLine";
import {CaptionOverlay} from "../components/text/CaptionOverlay";
import {OrganicLayer} from "../components/overlays/OrganicLayer";

/**
 * CAFÉ COM TEU ROI EP12 — "Problema de ordem, não de verba"
 * Editing Style: DYNAMIC (Dark Premium)
 * Evolução: Funil visual com gargalos animados, metáfora do balde
 */

const YELLOW = IDV.third;
const GREEN = IDV.secondary;
const RED = IDV.error;
const BLUE = IDV.primary;
const WHITE = "#f5f5f7";
const MUTED = "#a3a3a3";
const SZ = SAFE_ZONES.universal;

const SCENES = [
  {dur: 181, file: "audio/cafe-ep12/cafe_ep12_c01.mp3", dir: "right" as const},
  {dur: 269, file: "audio/cafe-ep12/cafe_ep12_c02.mp3", dir: "left" as const},
  {dur: 621, file: "audio/cafe-ep12/cafe_ep12_c03.mp3", dir: "up" as const},
  {dur: 192, file: "audio/cafe-ep12/cafe_ep12_c04.mp3", dir: "right" as const},
  {dur: 291, file: "audio/cafe-ep12/cafe_ep12_c05.mp3", dir: "up" as const},
];

export const CAFE_EP12_TOTAL = SCENES.reduce((s, sc) => s + sc.dur, 0);

const Particles: React.FC<{count: number; color: string}> = ({count, color}) => {
  const frame = useCurrentFrame();
  return (
    <>
      {Array.from({length: count}, (_, i) => {
        const x = random(`cpx12-${i}`) * 1080;
        const baseY = random(`cpy12-${i}`) * 1920;
        const size = 2 + random(`cps12-${i}`) * 3;
        const speed = 0.15 + random(`cpsp12-${i}`) * 0.3;
        const wrapped = ((baseY - frame * speed) % 2100 + 2100) % 2100 - 100;
        return (
          <div key={i} style={{
            position: "absolute", left: x, top: wrapped,
            width: size, height: size, borderRadius: "50%",
            background: color, opacity: 0.06 + random(`cpo12-${i}`) * 0.1,
          }} />
        );
      })}
    </>
  );
};

// ─── Pill badge ──────────────────────────────────────────────────────
const Pill: React.FC<{text: string; color: string; delay: number; icon?: string}> = ({text, color, delay, icon}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const p = spring({frame: frame - delay, fps, config: SPRINGS.snappy});
  if (frame < delay) return null;
  return (
    <div style={{
      opacity: p, transform: `scale(${interpolate(p, [0, 1], [0.7, 1], CLAMP)})`,
      padding: "10px 20px", borderRadius: 999, display: "flex", alignItems: "center", gap: 8,
      background: `${color}18`, border: `1px solid ${color}44`,
    }}>
      {icon && <span style={{fontSize: 28}}>{icon}</span>}
      <span style={{fontSize: FONT_SCALE.label.min, fontWeight: 700, color, fontFamily: FONT_STACK}}>{text}</span>
    </div>
  );
};

// ─── Funnel Step (gargalo visual) ────────────────────────────────────
const FunnelStep: React.FC<{
  label: string; problem: string; color: string; delay: number; broken?: boolean;
}> = ({label, problem, color, delay, broken}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const p = spring({frame: frame - delay, fps, config: SPRINGS.snappy});
  const shake = broken && frame > delay + 15
    ? Math.sin(frame * 0.6) * 2 * interpolate(frame - delay - 15, [0, 40], [1, 0], CLAMP)
    : 0;

  if (frame < delay) return null;
  return (
    <div style={{
      opacity: p,
      transform: `translateX(${interpolate(p, [0, 1], [30, 0], CLAMP) + shake}px)`,
      display: "flex", alignItems: "center", gap: 14, width: "90%",
    }}>
      <div style={{
        width: 12, height: 12, borderRadius: "50%",
        background: broken ? RED : color,
        boxShadow: broken ? `0 0 12px ${RED}66` : "none",
      }} />
      <div style={{flex: 1}}>
        <div style={{fontSize: FONT_SCALE.label.max, fontWeight: 700, color: WHITE, fontFamily: FONT_STACK}}>{label}</div>
        <div style={{fontSize: FONT_SCALE.label.min, fontWeight: 500, color: broken ? RED : MUTED, fontFamily: FONT_STACK}}>{problem}</div>
      </div>
      {broken && (
        <div style={{
          padding: "4px 12px", borderRadius: 999,
          background: `${RED}20`, border: `1px solid ${RED}44`,
          fontSize: 32, fontWeight: 800, color: RED, fontFamily: FONT_STACK,
        }}>
          ✗
        </div>
      )}
    </div>
  );
};

// ─── SCENE 1: ABERTURA ───────────────────────────────────────────────
const S1: React.FC = () => (
  <AbsoluteFill style={{justifyContent: "center", alignItems: "center", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`, flexDirection: "column", gap: 16}}>
    <Audio src={staticFile(SCENES[0].file)} volume={0.9} />
    <Pill text="☕ EP12" color={YELLOW} delay={5} />
    <GradientText text="CAFÉ COM TEU ROI" colors={[YELLOW, GREEN]} fontSize={FONT_SCALE.h1.min} springProfile="bouncy" />
    <div style={{marginTop: 8}}>
      <AccentLine delay={30} color={YELLOW} width={200} />
    </div>
  </AbsoluteFill>
);

// ─── SCENE 2: PRINCÍPIO ──────────────────────────────────────────────
const S2: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const p1 = spring({frame: frame - 10, fps, config: SPRINGS.heavy});
  const p2 = spring({frame: frame - 100, fps, config: SPRINGS.smooth});

  return (
    <OrganicLayer intensity="subtle" seed="cafe-ep12">
    <AbsoluteFill style={{justifyContent: "center", alignItems: "center", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`, flexDirection: "column", gap: 24}}>
      <Audio src={staticFile(SCENES[1].file)} volume={0.9} />
      <Pill text="PRINCÍPIO DO DIA" color={GREEN} delay={5} icon="💡" />
      <div style={{
        opacity: p1, transform: `translateY(${interpolate(p1, [0, 1], [20, 0], CLAMP)}px)`,
        fontSize: FONT_SCALE.h2.min, fontWeight: 700, color: WHITE,
        fontFamily: FONT_STACK, textAlign: "center", lineHeight: 1.2, maxWidth: "88%",
      }}>
        Investir mais antes{"\n"}de consertar o que tem?
      </div>
      <div style={{
        opacity: p2, transform: `translateY(${interpolate(p2, [0, 1], [15, 0], CLAMP)}px)`,
        fontSize: FONT_SCALE.body.min, fontWeight: 500, color: YELLOW,
        fontFamily: FONT_STACK, textAlign: "center", fontStyle: "italic",
      }}>
        Gasolina num carro com pneu furado.
      </div>
    </AbsoluteFill>
    </OrganicLayer>
  );
};

// ─── SCENE 3: REFLEXÃO — Funil com gargalos ──────────────────────────
const S3: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();

  // Fase A (0-250): Funil com problemas
  // Fase B (250-450): "Não é verba. É ordem."
  // Fase C (450+): Metáfora do balde
  const showFunnel = frame < 270;
  const showOrder = frame >= 250 && frame < 470;
  const showBucket = frame >= 450;

  const orderP = spring({frame: frame - 260, fps, config: SPRINGS.heavy});
  const bucketP = spring({frame: frame - 460, fps, config: SPRINGS.heavy});

  return (
    <AbsoluteFill style={{justifyContent: "center", alignItems: "center", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`, flexDirection: "column", gap: 16}}>
      <Audio src={staticFile(SCENES[2].file)} volume={0.9} />

      {/* Fase A: Funil com gargalos */}
      {showFunnel && (
        <div style={{display: "flex", flexDirection: "column", gap: 14, alignItems: "center", width: "100%"}}>
          <div style={{
            fontSize: FONT_SCALE.label.max, fontWeight: 600, color: MUTED,
            fontFamily: FONT_STACK, opacity: spring({frame: frame - 5, fps, config: SPRINGS.smooth}),
          }}>
            SEU FUNIL HOJE
          </div>
          <FunnelStep label="Site" problem="8s pra carregar" color={RED} delay={30} broken />
          <FunnelStep label="Formulário" problem="Não funciona no celular" color={RED} delay={60} broken />
          <FunnelStep label="Comercial" problem="3 dias pra responder" color={RED} delay={90} broken />
          <FunnelStep label="Tráfego" problem="+ verba" color={GREEN} delay={120} />
        </div>
      )}

      {/* Fase B: Texto impacto */}
      {showOrder && (
        <div style={{opacity: orderP, textAlign: "center"}}>
          <GradientText text="NÃO É VERBA." colors={[RED, YELLOW]} fontSize={FONT_SCALE.h1.min} springProfile="heavy" />
          <div style={{
            opacity: spring({frame: frame - 310, fps, config: SPRINGS.smooth}),
            fontSize: FONT_SCALE.h2.min, fontWeight: 700, color: GREEN,
            fontFamily: FONT_STACK, marginTop: 12,
          }}>
            É ordem.
          </div>
        </div>
      )}

      {/* Fase C: Metáfora do balde */}
      {showBucket && (
        <div style={{opacity: bucketP, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 16}}>
          <div style={{fontSize: FONT_SCALE.h2.min, fontWeight: 700, color: WHITE, fontFamily: FONT_STACK, lineHeight: 1.2}}>
            Primeiro conserta o balde.
          </div>
          <GlowText text="Depois joga água." delay={500} size={FONT_SCALE.h2.max} color={GREEN} />
        </div>
      )}
    </AbsoluteFill>
  );
};

// ─── SCENE 4: COMPROMISSO ─────────────────────────────────────────────
const S4: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const p1 = spring({frame: frame - 10, fps, config: SPRINGS.heavy});

  return (
    <AbsoluteFill style={{justifyContent: "center", alignItems: "flex-start", padding: `${SZ.top}px ${SZ.left + 20}px ${SZ.bottom}px`, flexDirection: "column", gap: 20}}>
      <Audio src={staticFile(SCENES[3].file)} volume={0.9} />
      <Pill text="COMPROMISSO" color={YELLOW} delay={5} icon="🤝" />
      <div style={{
        opacity: p1, transform: `translateX(${interpolate(p1, [0, 1], [-30, 0], CLAMP)}px)`,
        fontSize: FONT_SCALE.h2.min, fontWeight: 700, color: WHITE,
        fontFamily: FONT_STACK, lineHeight: 1.2, maxWidth: "92%",
      }}>
        Olhar o funil inteiro.{"\n"}Do clique ao fechamento.
      </div>
    </AbsoluteFill>
  );
};

// ─── SCENE 5: DESAFIO + CTA ──────────────────────────────────────────
const S5: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const challengeP = spring({frame: frame - 8, fps, config: SPRINGS.heavy});
  const profileP = spring({frame: frame - 100, fps, config: SPRINGS.snappy});
  const closeP = spring({frame: frame - 180, fps, config: SPRINGS.smooth});

  return (
    <AbsoluteFill style={{justifyContent: "center", alignItems: "center", gap: 20, flexDirection: "column", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`}}>
      <Audio src={staticFile(SCENES[4].file)} volume={0.9} />

      <Pill text="DESAFIO" color={GREEN} delay={5} icon="🎯" />
      <div style={{
        opacity: challengeP, transform: `translateY(${interpolate(challengeP, [0, 1], [20, 0], CLAMP)}px)`,
        fontSize: FONT_SCALE.body.min, fontWeight: 600, color: WHITE,
        fontFamily: FONT_STACK, textAlign: "center", lineHeight: 1.3, maxWidth: "88%",
      }}>
        Pega seu celular.{"\n"}Clica no teu anúncio.{"\n"}Faz o caminho do lead.
      </div>

      <div style={{
        opacity: profileP, display: "flex", alignItems: "center", gap: 16,
        transform: `translateY(${interpolate(profileP, [0, 1], [20, 0], CLAMP)}px)`,
      }}>
        <div style={{
          width: 56, height: 56, borderRadius: "50%", overflow: "hidden",
          border: `3px solid ${GREEN}`, boxShadow: `0 0 20px ${GREEN}30`,
        }}>
          <Img src={staticFile("assets/profiles/guilhermeribeiro-ai.jpg")}
            style={{width: "100%", height: "100%", objectFit: "cover"}} />
        </div>
        <div>
          <div style={{fontSize: FONT_SCALE.body.min, fontWeight: 700, color: WHITE, fontFamily: FONT_STACK}}>@guilhermeribeiro.ai</div>
          <div style={{fontSize: FONT_SCALE.label.min, fontWeight: 400, color: MUTED, fontFamily: FONT_STACK}}>Café com teu ROI</div>
        </div>
      </div>

      <div style={{
        opacity: closeP, fontSize: FONT_SCALE.body.min, fontWeight: 500,
        color: MUTED, fontFamily: FONT_STACK, fontStyle: "italic",
      }}>
        Até amanhã.
      </div>
    </AbsoluteFill>
  );
};

const SceneFade: React.FC<{duration: number; children: React.ReactNode}> = ({duration, children}) => {
  const frame = useCurrentFrame();
  const fadeIn = interpolate(frame, [0, 8], [0, 1], CLAMP);
  const fadeOut = interpolate(frame, [duration - 10, duration], [1, 0], CLAMP);
  return <AbsoluteFill style={{opacity: Math.min(fadeIn, fadeOut)}}>{children}</AbsoluteFill>;
};

export const CafeEp12: React.FC = () => {
  let offset = 0;
  return (
    <AbsoluteFill style={{backgroundColor: "#000"}}>
      <div style={{position: "absolute", inset: 0, background: `radial-gradient(ellipse at 50% 30%, ${BLUE}22, #000 70%)`}} />
      <Noise opacity={0.02} />
      <Particles count={20} color={GREEN} />
      <FilmGrain opacity={0.03} />
      <Vignette intensity={0.6} />

      {SCENES.map(({dur, dir}, i) => {
        const from = offset;
        offset += dur;
        const Comp = [S1, S2, S3, S4, S5][i];
        return (
          <Sequence key={i} from={from} durationInFrames={dur}>
            <SceneFade duration={dur}>
              <SceneTransition type="in" direction={dir} springProfile="smooth">
                <Comp />
              </SceneTransition>
            </SceneFade>
          </Sequence>
        );
      })}

      {/* Closed Captions */}
      <CaptionOverlay
        captionsSource="captions.json"
        preset="bold"
        position="top"
        fontSize={52}
        fontFamily="Inter"
        textColor="rgba(255,255,255,0.7)"
        highlightColor={YELLOW}
        combineTokensWithinMs={800}
        style={{top: 200}}
      />
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
